import React, { useState, useEffect } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CButton,
  CAlert,
  CFormCheck
} from '@coreui/react';

const ResellerModal = ({ 
  show, 
  onClose, 
  onSave, 
  reseller = null,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [formData, setFormData] = useState({
    company_name: '',
    admin_login_email: '',
    password: '',
    confirm_password: '',
    admin_contact: '',
    phone: '',
    payment_mode: 'Net 30',
    location: '',
    margin: '25'
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  // Prefill form when editing
  useEffect(() => {
    if (reseller && mode === 'edit') {
      setFormData({
        company_name: reseller.company_name || '',
        admin_login_email: reseller.admin_login_email || '',
        password: '',
        confirm_password: '',
        admin_contact: reseller.admin_contact || '',
        phone: reseller.phone || '',
        payment_mode: reseller.payment_mode || 'Net 30',
        location: reseller.location || '',
        margin: reseller.margin?.toString() || '25',
        is_active: reseller.is_active ?? true
      });
    } else {
      // Reset form for create mode
      setFormData({
        company_name: '',
        admin_login_email: '',
        password: '',
        confirm_password: '',
        admin_contact: '',
        phone: '',
        payment_mode: 'Net 30',
        location: '',
        margin: '25',
        is_active: true
      });
    }
    setErrors({});
    setServerError('');
  }, [reseller, mode, show]);

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    const requiredFields = [
      'company_name', 'admin_login_email', 'admin_contact', 
      'phone', 'location'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `${field.replace('_', ' ')} is required`;
      }
    });
    
    // Email validation
    if (formData.admin_login_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_login_email)) {
      newErrors.admin_login_email = 'Invalid email format';
    }
    
    // Password validation for create mode
    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }
    
    // Password validation for edit mode (only if provided)
    if (mode === 'edit' && formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }
    
    // Phone validation
    if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    
    // Margin validation
    if (formData.margin) {
      const margin = parseFloat(formData.margin);
      if (isNaN(margin) || margin < 0 || margin > 100) {
        newErrors.margin = 'Margin must be between 0 and 100';
      }
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setLoading(true);
    setServerError('');
    
    try {
      // Prepare data for API
      const submitData = {
        company_name: formData.company_name.trim(),
        admin_login_email: formData.admin_login_email.trim(),
        admin_contact: formData.admin_contact.trim(),
        phone: formData.phone.trim(),
        payment_mode: formData.payment_mode,
        location: formData.location.trim(),
        margin: parseFloat(formData.margin),
        is_active: formData.is_active
      };
      
      // Only include password if provided (for edit) or always (for create)
      if (mode === 'create' || (mode === 'edit' && formData.password)) {
        submitData.password = formData.password;
      }
      
      await onSave(submitData, mode);
      onClose();
    } catch (error) {
      setServerError(error.response?.data?.error || 'Failed to save reseller');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <CModal 
      visible={show} 
      onClose={onClose}
      size="lg"
      backdrop="static"
    >
      <CModalHeader>
        <CModalTitle>
          {mode === 'create' ? 'Add New Reseller' : 'Edit Reseller'}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {serverError && (
          <CAlert color="danger" className="mb-3">
            {serverError}
          </CAlert>
        )}
        
        <CForm onSubmit={handleSubmit} id="resellerForm">
          <div className="mb-3">
            <CFormLabel htmlFor="company_name">Company Name *</CFormLabel>
            <CFormInput
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              placeholder="Enter company name"
              invalid={!!errors.company_name}
            />
            {errors.company_name && (
              <div className="invalid-feedback d-block">
                {errors.company_name}
              </div>
            )}
          </div>
          
          <div className="mb-3">
            <CFormLabel htmlFor="admin_login_email">Admin Email *</CFormLabel>
            <CFormInput
              type="email"
              id="admin_login_email"
              name="admin_login_email"
              value={formData.admin_login_email}
              onChange={handleChange}
              placeholder="Enter admin email"
              invalid={!!errors.admin_login_email}
            />
            {errors.admin_login_email && (
              <div className="invalid-feedback d-block">
                {errors.admin_login_email}
              </div>
            )}
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <CFormLabel htmlFor="password">
                {mode === 'create' ? 'Password *' : 'Password (leave blank to keep current)'}
              </CFormLabel>
              <CFormInput
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={mode === 'create' ? 'Enter password' : 'Enter new password'}
                invalid={!!errors.password}
              />
              {errors.password && (
                <div className="invalid-feedback d-block">
                  {errors.password}
                </div>
              )}
            </div>
            <div className="col-md-6">
              <CFormLabel htmlFor="confirm_password">
                {mode === 'create' ? 'Confirm Password *' : 'Confirm New Password'}
              </CFormLabel>
              <CFormInput
                type="password"
                id="confirm_password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder={mode === 'create' ? 'Confirm password' : 'Confirm new password'}
                invalid={!!errors.confirm_password}
              />
              {errors.confirm_password && (
                <div className="invalid-feedback d-block">
                  {errors.confirm_password}
                </div>
              )}
            </div>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <CFormLabel htmlFor="admin_contact">Contact Person *</CFormLabel>
              <CFormInput
                id="admin_contact"
                name="admin_contact"
                value={formData.admin_contact}
                onChange={handleChange}
                placeholder="Enter contact person name"
                invalid={!!errors.admin_contact}
              />
              {errors.admin_contact && (
                <div className="invalid-feedback d-block">
                  {errors.admin_contact}
                </div>
              )}
            </div>
            <div className="col-md-6">
              <CFormLabel htmlFor="phone">Phone Number *</CFormLabel>
              <CFormInput
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                invalid={!!errors.phone}
              />
              {errors.phone && (
                <div className="invalid-feedback d-block">
                  {errors.phone}
                </div>
              )}
            </div>
          </div>
          
          <div className="row mb-3">
            <div className="col-md-6">
              <CFormLabel htmlFor="payment_mode">Payment Mode</CFormLabel>
              <CFormSelect
                id="payment_mode"
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
              >
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
                <option value="Credit Card">Credit Card</option>
              </CFormSelect>
            </div>
            <div className="col-md-6">
              <CFormLabel htmlFor="margin">Margin (%)</CFormLabel>
              <CFormInput
                type="number"
                id="margin"
                name="margin"
                value={formData.margin}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.1"
                placeholder="Enter margin percentage"
                invalid={!!errors.margin}
              />
              {errors.margin && (
                <div className="invalid-feedback d-block">
                  {errors.margin}
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-3">
            <CFormLabel htmlFor="location">Address/Location *</CFormLabel>
            <CFormTextarea
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter complete address"
              rows="3"
              invalid={!!errors.location}
            />
            {errors.location && (
              <div className="invalid-feedback d-block">
                {errors.location}
              </div>
            )}
          </div>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </CButton>
        <CButton 
          color="primary" 
          type="submit" 
          form="resellerForm"
          disabled={loading}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create Reseller' : 'Update Reseller'}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default ResellerModal;